import _ from 'lodash';
import * as moment from 'moment';

import { ElementRef, Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { fromEvent, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { AccordionService } from './campaign-accordion.service';
import { CampaignService } from './campaign.service';
import { CampaignTabsStepService } from './campaign-tabs-step.service';
import { CampaignOptionListService } from './campaign-option-list.service';
import { CampaignOptionListEventHandlerService } from './campaign-option-list-event-handler.service';
import { ToastService } from 'app/toast/services/toast.service';
import { createCampaign, putCampaign } from '../features/campaign.actions';
import { FormService } from './form.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class CampaignFormService {
    public container: ElementRef = null;
    public raw: any = {
        id: null,
        campaignTypeData: {
            presetOptions: []
        }
    };

    public form: UntypedFormGroup = null;
    public campaignTypeData: UntypedFormGroup = null;
    public schema: any = null;

    public submitted: boolean = false;

    get dirty():boolean {
        return this.form.dirty;
    }

    get invalid():boolean {
        return this.form.invalid;
    }

    get valid():boolean {
        return this.form.valid;
    }

    public takeUntilSubject: Subject<any> = new Subject();

    constructor(
        private router: Router,
        public store: Store,
        public accordionService: AccordionService,
        public campaignTabsStepService: CampaignTabsStepService,
        public campaignOptionListService: CampaignOptionListService,
        public campaignOptionListEventHandlerService: CampaignOptionListEventHandlerService,
        public campaignService: CampaignService,
        public formService: FormService,
        public fb: UntypedFormBuilder,
        public toastService: ToastService
    ) {
        this.campaignOptionListEventHandlerService.onUpdateCampaignOptionList.pipe(
          map(list => {
            return _.map(list, ({ data: option }) => {
              if(_.has(option, 'pujaMultiDates')) {
                option.pujaMultiDates = _.map(option.pujaMultiDates, value => {
                  return moment(value).format('YYYY/MM/DD');
                });
              }
              if(_.has(option, 'pujaDate')) {
                option.pujaDate = _.isEmpty(option.pujaDate) ? '': moment(option.pujaDate).format('YYYY/MM/DD')
              }
              return {
                ...option,
                id: _.isNumber(option.id) ? null: option.id
              };
            });
          }),
          takeUntil(this.takeUntilSubject)
        ).subscribe(list => {
          this.raw.campaignTypeData.presetOptions = list;
        });
    }

    private _getTopOffset(element: HTMLElement): number {
        const labelOffset = 50;
        return element.getBoundingClientRect().top + window.scrollY - labelOffset;
    }

    private _scrollToElement(element: HTMLElement) {
      fromEvent(window, "scroll").pipe(
        take(1)
      ).subscribe(() => element.focus());
  
      window.scroll({
        top: this._getTopOffset(element),
        left: 0,
        behavior: "smooth"
      });
    }
    
    generate() {
        this.campaignService.generateForm(this.form, this.schema);

        this.campaignTypeData = this.form.get('campaignTypeData') as UntypedFormGroup;
    }

    initialize(campaign) {
        this.raw = campaign;
        this.campaignService.initializeForm(this.form, {
            ...campaign, campaignTypeData: {
                ...campaign.campaignTypeData, presetOptions: []
            }
        }, this.schema);
        if(this.form) {
          this.form.markAsPristine();
        }
    }

    save(options) {
        const { step } = this.campaignTabsStepService;
        const { activeList } = this.campaignOptionListService;

        const name = this.form.get('name');
        const trimedName = _.trim(name.value);
        name.setValue(trimedName);

        const isValid = this.formService.isFormValid(this.form, []);
        if(!isValid) {
          this.isNotValid();
          return false;
        }
        
        if (options?.checkCustom) {
          const hideCustom = _.get(this.campaignTypeData.controls, 'hideCustom.value', true);

          if(hideCustom) {
            if(activeList.length < 1) {
              if(step === 0) {
                this.campaignTabsStepService.nextStep();
              }
              this.toastService.show('danger', 'Please add Campaign Option', {delay: 10000});
              return false;
            }
          }
        }
    
        const id = this.raw.id;
        const type = id ? putCampaign.type: createCampaign.type;
        const payload: any = this.campaignService.generateRequestObject(this.form, this.schema, this.raw);
        payload.campaignTypeData.presetOptions = _.sortBy(payload.campaignTypeData.presetOptions, [function(option) {
          return option.id === null;
        }]);
        const redirect = options?.redirect;
        this.store.dispatch({ type, payload: { ...payload, id }, redirect });
    }

    forceSave(redirect) {
      const id = this.raw.id;
      const type = id ? putCampaign.type: createCampaign.type;
      const payload: any = this.campaignService.generateRequestObject(this.form, this.schema, this.raw);
      payload.campaignTypeData.presetOptions = _.sortBy(payload.campaignTypeData.presetOptions, [function(option) {
        return option.id === null;
      }]);
      return this.store.dispatch({ type, payload: { ...payload, id }, redirect });
    }

    enableBasketRaffle() {
      const id = this.raw.id;
      if(id === null) {
        return;
      }

      const { campaignTypeData } = this.raw;
      campaignTypeData.basketRaffle = true;
      campaignTypeData.sendTickets = false;
      campaignTypeData.guestRequired = false;
      campaignTypeData.saveCardEnabled = false;
      campaignTypeData.isTicketForInPersonEvent = false;
      campaignTypeData.isTicketForVirtualEvent = false;

      const form = this.fb.group({});
      this.campaignService.generateForm(form, this.schema);
      this.campaignService.initializeForm(form, {
        ...this.raw, campaignTypeData: {
            ...this.raw.campaignTypeData, presetOptions: []
        }
      }, this.schema);

      const payload: any = this.campaignService.generateRequestObject(form, this.schema, this.raw);
      payload.campaignTypeData.presetOptions = _.sortBy(payload.campaignTypeData.presetOptions, [function(option) {
        return option.id === null;
      }]);
      return this.store.dispatch({ type: putCampaign.type, payload: { ...payload, id }, redirect: false, update: false });
    }
    
    isNotValid() {
        const { form } = this;
    
        form.markAllAsTouched();
        this.submitted = true;
    
        const element: HTMLElement = this.container.nativeElement.querySelector(".ng-invalid");
        if(!element) {
          return false;
        }
        const panel = _.get(element.dataset, 'panel', null);
        const accordionItem = _.get(this.accordionService.map, panel, null);

        if(accordionItem) {
          if(accordionItem.expanded) {
            this._scrollToElement(element);
          } else {
            accordionItem.expanded = true;
            this.accordionService.one('afterExpand', () => {
              this._scrollToElement(element);
            });
          }
        } else {
          this._scrollToElement(element);
        }
    
        return false;
    }

    public reset() {
        this.container = null;
        this.raw = {
            id: null,
            campaignTypeData: {
                presetOptions: []
            }
        };

        this.form = null;
        this.campaignTypeData = null;

        this.submitted = false;
    }
}
